/**
 * Application configuration and feature flags
 * Centralized configuration management for the ERP application
 */

// Environment variables with fallbacks
export const config = {
  // API Configuration
  api: {
    baseUrl: import.meta.env.VITE_API_URL || 'https://erpapi2026.erpaga.com',
    timeout: 90000, // 90 seconds (1.5 minutes)
  },

  // Application Information
  app: {
    name: import.meta.env.VITE_APP_NAME || 'MyERP',
    version: '1.0.1',
    environment: import.meta.env.MODE || 'development',
  },

  // Feature Flags
  features: {
    useMockData: import.meta.env.VITE_USE_MOCK_DATA === 'true',
    enableDebug: import.meta.env.VITE_ENABLE_DEBUG === 'true',
    enableAnalytics: import.meta.env.VITE_ENABLE_ANALYTICS === 'true',
    enableNotifications: true,
    enableRealTimeUpdates: false,
    enableAdvancedSearch: true,
    enableDataExport: true,
    enableBulkOperations: true,
  },

  // UI Configuration
  ui: {
    defaultPageSize: 10,
    maxPageSize: 100,
    sidebarWidth: 256,
    sidebarCollapsedWidth: 64,
    debounceDelay: 300,
    autoRefreshInterval: 30000, // 30 seconds
  },

  // Localization
  localization: {
    defaultLanguage: 'en',
    supportedLanguages: ['en', 'ar'],
    rtlLanguages: ['ar', 'he', 'fa', 'ur'],
    fallbackLanguage: 'en',
  },

  // Authentication
  auth: {
    tokenKey: 'auth-token',
    refreshTokenKey: 'refresh-token',
    sessionTimeout: 30 * 60 * 1000, // 30 minutes
    refreshThreshold: 5 * 60 * 1000, // 5 minutes before expiry
  },

  // Validation Rules
  validation: {
    email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    phone: /^\+?[\d\s\-\(\)]+$/,
    password: {
      minLength: 8,
      requireNumbers: true,
      requireSymbols: true,
      requireUppercase: true,
      requireLowercase: true,
    },
  },

  // File Upload
  upload: {
    maxFileSize: 10 * 1024 * 1024, // 10MB
    allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'application/pdf', 'text/csv'],
    allowedExtensions: ['.jpg', '.jpeg', '.png', '.gif', '.pdf', '.csv'],
  },

  // Date and Number Formats
  formats: {
    date: {
      short: 'MM/DD/YYYY',
      long: 'MMMM DD, YYYY',
      time: 'HH:mm',
      datetime: 'MM/DD/YYYY HH:mm',
    },
    currency: {
      default: 'USD',
      precision: 2,
    },
    number: {
      thousandsSeparator: ',',
      decimalSeparator: '.',
    },
  },

  // Chart Colors (for consistent theming)
  charts: {
    colors: {
      primary: '#3b82f6',
      secondary: '#64748b',
      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444',
      info: '#06b6d4',
    },
    gradients: {
      primary: ['#3b82f6', '#1d4ed8'],
      secondary: ['#64748b', '#475569'],
      success: ['#10b981', '#059669'],
    },
  },
} as const

// Type for feature flags to ensure type safety
export type FeatureFlag = keyof typeof config.features

// Helper functions
export const isFeatureEnabled = (feature: FeatureFlag): boolean => {
  return config.features[feature]
}

export const isDevelopment = (): boolean => {
  return config.app.environment === 'development'
}

export const isProduction = (): boolean => {
  return config.app.environment === 'production'
}

export const getApiUrl = (endpoint: string): string => {
  return `${config.api.baseUrl}${endpoint}`
}

// Runtime configuration that can be modified
export const runtimeConfig = {
  // Theme preferences (will be managed by UI store)
  theme: 'system' as 'light' | 'dark' | 'system',
  
  // Language preferences (will be managed by settings store)
  language: config.localization.defaultLanguage,
  
  // Debug flags that can be toggled at runtime
  debug: {
    apiLogging: config.features.enableDebug,
    stateLogging: config.features.enableDebug,
    performanceLogging: false,
  },
}

export default config 